import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, MessageSquare, MoreVertical, Users, Star, MapPin, Calendar, Phone, Mail, Award, Check, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../services/userService';
import { showToast } from '../../components/Toast';
import { showConfirm } from '../../components/ConfirmDialog';

const TrainerDetailPage: React.FC = () => {
  const { trainerId } = useParams<{ trainerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: trainer, isLoading } = useQuery({
    queryKey: ['trainerDetail', trainerId],
    queryFn: () => trainerId ? userService.getTrainerDetails(trainerId) : Promise.reject('No trainer ID'),
  });

  // Mutation for updating trainer status
  const updateStatusMutation = useMutation({
    mutationFn: ({ trainerId, status }: { trainerId: string; status: 'Active' | 'Pending' | 'Banned' }) =>
      userService.updateTrainerStatus(trainerId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainerDetail', trainerId] });
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
    },
    onError: (error: any) => {
      showToast(error.message || 'Failed to update trainer status', 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading trainer details...</div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Trainer not found</div>
      </div>
    );
  }

  const firstName = trainer.firstName || 'Trainer';
  const lastName = trainer.lastName || '';
  const firstNameChar = firstName.charAt(0).toUpperCase();
  const lastNameChar = lastName.charAt(0).toUpperCase();
  const profilePhotoUrl = trainer.profilePhoto;
  const instructorProfile = trainer.instructorProfile || {};
  const accountStatus = trainer.accountStatus || 'Active';

  const handleApproveTrainer = () => {
    updateStatusMutation.mutate({ trainerId: trainer._id, status: 'Active' });
  };

  const handleRejectTrainer = () => {
    showConfirm({
      title: 'Reject Trainer',
      message: `Are you sure you want to reject ${firstName} ${lastName}? This action cannot be undone.`,
      confirmText: 'Reject',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: () => updateStatusMutation.mutate({ trainerId: trainer._id, status: 'Banned' }),
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500 bg-opacity-20 text-green-400';
      case 'Pending':
        return 'bg-yellow-500 bg-opacity-20 text-yellow-400';
      case 'Banned':
        return 'bg-red-500 bg-opacity-20 text-red-400';
      default:
        return 'bg-gray-500 bg-opacity-20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainers')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#23F47C] transition-all mb-8"
        >
          <ArrowLeft size={20} />
          Back to Trainers
        </button>

        {/* Header Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6 flex-1">
              {/* Profile Picture */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 border-2 border-gray-700">
                  {profilePhotoUrl ? (
                    <img
                      src={profilePhotoUrl}
                      alt={firstName}
                      className="w-32 h-32 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-gray-400 text-4xl font-bold">
                      {firstNameChar}{lastNameChar}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-[#23F47C] rounded-full border-3 border-gray-900"></div>
              </div>

              {/* Trainer Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-4xl font-bold text-white">{firstName} {lastName}</h1>
                  <span className={`px-4 py-2 text-xs font-bold rounded-full ${getStatusColor(accountStatus)}`}>
                    {accountStatus}
                  </span>
                </div>
                <p className="text-gray-400 mb-4 text-lg">Trainer ID: #TR-{trainer._id?.slice(-4).toUpperCase() || 'N/A'}</p>
                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-[#23F47C]" />
                    Joined {trainer.createdAt ? new Date(trainer.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : 'N/A'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={18} className="text-[#23F47C]" />
                    {trainer.clientCount || 0} Active Clients
                  </div>
                  <div className="flex items-center gap-2">
                    <Star size={18} className="text-[#23F47C]" />
                    {trainer.rating ? `${trainer.rating.toFixed(1)} Rating` : 'No Rating'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {accountStatus === 'Pending' && (
                <>
                  <button 
                    onClick={handleApproveTrainer}
                    disabled={updateStatusMutation.isPending}
                    className="flex items-center gap-2 bg-green-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-600 transition-all disabled:opacity-50"
                  >
                    <Check size={18} />
                    Approve
                  </button>
                  <button 
                    onClick={handleRejectTrainer}
                    disabled={updateStatusMutation.isPending}
                    className="flex items-center gap-2 bg-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                  >
                    <X size={18} />
                    Reject
                  </button>
                </>
              )}
              <button 
                title="Edit Profile"
                className="p-3 text-gray-400 hover:text-[#23F47C] transition-all"
              >
                <Edit2 size={20} />
              </button>
              <button 
                title="Message"
                className="p-3 text-gray-400 hover:text-[#23F47C] transition-all"
              >
                <MessageSquare size={20} />
              </button>
              <button className="p-3 text-gray-400 hover:text-[#23F47C] transition-all">
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Professional Info */}
          <div className="col-span-2 space-y-8">
            {/* Professional Information */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <div className="w-1 h-8 bg-[#23F47C] rounded"></div>
                Professional Information
              </h2>

              <div className="grid grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Email Address</p>
                  <p className="text-white font-medium text-lg">{trainer.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Phone Number</p>
                  <p className="text-white font-medium text-lg">{trainer.phone || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Experience</p>
                  <p className="text-white font-medium text-lg">
                    {instructorProfile.experienceYears ? `${instructorProfile.experienceYears} years` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Location</p>
                  <p className="text-white font-medium text-lg">{trainer.location || trainer.gymName || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Specialties */}
            {instructorProfile.specialties && instructorProfile.specialties.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <Award size={24} className="text-[#23F47C]" />
                  Specialties & Skills
                </h3>
                <div className="flex flex-wrap gap-3">
                  {instructorProfile.specialties.map((specialty: string, index: number) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#23F47C] bg-opacity-20 text-[#23F47C] rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bio */}
            {instructorProfile.bio && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <MessageSquare size={24} className="text-[#23F47C]" />
                  About
                </h3>
                <p className="text-gray-300 text-base leading-relaxed">
                  {instructorProfile.bio}
                </p>
              </div>
            )}

            {/* Certifications */}
            {instructorProfile.certifications && instructorProfile.certifications.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <Award size={24} className="text-[#23F47C]" />
                  Certifications
                </h3>
                <div className="space-y-3">
                  {instructorProfile.certifications.map((cert: string, index: number) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                      <Award size={20} className="text-[#23F47C]" />
                      <span className="text-white font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Quick Actions & Stats */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-bold text-white mb-6">Quick Actions</h3>
              <div className="space-y-4">
                <button className="w-full flex flex-col items-center gap-3 p-6 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all">
                  <MessageSquare size={28} className="text-[#23F47C]" />
                  <span className="text-base font-medium text-white">Send Message</span>
                </button>
                <button className="w-full flex flex-col items-center gap-3 p-6 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all">
                  <Users size={28} className="text-[#23F47C]" />
                  <span className="text-base font-medium text-white">View Clients</span>
                </button>
                <button className="w-full flex flex-col items-center gap-3 p-6 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all">
                  <Calendar size={28} className="text-[#23F47C]" />
                  <span className="text-base font-medium text-white">Schedule</span>
                </button>
              </div>
            </div>

            {/* Performance Stats */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-bold text-white mb-6">Performance</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Client Satisfaction</span>
                    <span className="text-white font-semibold">{trainer.rating ? `${trainer.rating.toFixed(1)}/5.0` : 'N/A'}</span>
                  </div>
                  {trainer.rating && (
                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#23F47C]" 
                        style={{ width: `${(trainer.rating / 5) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Active Clients</span>
                    <span className="text-white font-semibold">{trainer.clientCount || 0}</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Experience</span>
                    <span className="text-white font-semibold">
                      {instructorProfile.experienceYears ? `${instructorProfile.experienceYears} years` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-bold text-white mb-6">Contact</h3>
              <div className="space-y-4">
                {trainer.email && (
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-[#23F47C]" />
                    <span className="text-gray-300">{trainer.email}</span>
                  </div>
                )}
                {trainer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-[#23F47C]" />
                    <span className="text-gray-300">{trainer.phone}</span>
                  </div>
                )}
                {(trainer.location || trainer.gymName) && (
                  <div className="flex items-center gap-3">
                    <MapPin size={20} className="text-[#23F47C]" />
                    <span className="text-gray-300">{trainer.location || trainer.gymName}</span>
                  </div>
                )}
                {instructorProfile.linkedinLink && (
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-[#23F47C]" />
                    <a 
                      href={instructorProfile.linkedinLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#23F47C] hover:underline"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerDetailPage;